import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Save, Check, AlertCircle, AlertTriangle, Copy, CalendarRange, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useWeeklyTemplate, useSaveWeeklyTemplate, useReclaimManualDays, useSites,
  type ManualDay,
} from '@/api/resources';
import {
  opensOnSunday, slotDefaults, SLOT_FALLBACK,
  type OpeningDay, type SlotDefaults,
} from '@/lib/schedule';
import type { ScheduleMode, TemplateWeekType, WeeklyTemplateDay } from '@/types';

// Jours ouvrés habituels : Lundi → Samedi, comme la grille de planning. Le
// dimanche s'y ajoute quand l'officine ouvre ce jour-là (voir `daysToShow`).
const WEEKDAYS = [
  { v: 1, l: 'Lundi' },
  { v: 2, l: 'Mardi' },
  { v: 3, l: 'Mercredi' },
  { v: 4, l: 'Jeudi' },
  { v: 5, l: 'Vendredi' },
  { v: 6, l: 'Samedi' },
];
const SUNDAY = { v: 0, l: 'Dimanche' };
const DAY_NAMES = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** « lundi », « lundi et jeudi », « lundi, mardi et jeudi ». */
function listDays(days: number[]): string {
  const names = days.map((d) => DAY_NAMES[d]);
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
}

/**
 * Périmètre d'édition de la semaine type d'un salarié partagé (lot 3B) :
 * l'identifiant d'une officine, ou `ALL` pour les voir toutes ensemble.
 * On désigne toujours la pharmacie de rattachement par son **id**, jamais par
 * le `null` de la base : la conversion se fait au bord, à l'écriture.
 */
export type SiteView = string | 'ALL';

/**
 * Les officines entre lesquelles répartir les jours, celle de rattachement en
 * tête. Son nom n'est pas dans la réponse `weekly-template` (qui n'en donne
 * que l'id) : il vient de la liste du groupe, déjà en cache pour le sélecteur
 * de la barre latérale — donc sans appel supplémentaire.
 * Liste vide pour un salarié mono-site : ni bandeau, ni sélecteur.
 */
function useTemplateSites(employeeId: string | undefined) {
  const { data } = useWeeklyTemplate(employeeId);
  const { data: group } = useSites();
  return useMemo(() => {
    const homeId = data?.homePharmacyId;
    if (!data?.multiSite || !homeId || !data.sites?.length) {
      return { homeId, choices: [] as { id: string; name: string }[] };
    }
    const homeName =
      group?.sites.find((s) => s.id === homeId)?.name ?? 'Pharmacie de rattachement';
    return { homeId, choices: [{ id: homeId, name: homeName }, ...data.sites] };
  }, [data, group]);
}

/**
 * Bandeau de choix d'officine, au-dessus de « Horaires & contraintes ».
 * Il ne filtre pas un affichage : il désigne **dans quelle officine on saisit**,
 * pour que toutes les heures du salarié se règlent depuis ce seul écran.
 */
export function SiteScopeBar({
  employeeId,
  value,
  onChange,
}: {
  employeeId: string;
  value: SiteView;
  onChange: (v: SiteView) => void;
}) {
  const { choices } = useTemplateSites(employeeId);
  if (choices.length < 2) return null;

  const items: { id: SiteView; label: string; hint: string }[] = [
    ...choices.map((c) => ({
      id: c.id as SiteView,
      label: c.name,
      hint: 'Ses jours dans cette officine',
    })),
    { id: 'ALL', label: 'Global multi-site', hint: 'La semaine entière, tous sites' },
  ];

  return (
    <div className="mb-5">
      <div className="label mb-2">Officine</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {items.map((it) => {
          const active = it.id === value;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onChange(it.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                active
                  ? 'bg-primary border-primary text-white shadow-sm'
                  : 'bg-white border-primary/30 text-primary hover:border-primary'
              }`}
            >
              <div className="text-[13px] font-semibold">{it.label}</div>
              <div className={`text-[11px] mt-0.5 ${active ? 'text-white/70' : 'text-slate-500'}`}>
                {it.hint}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface DayState {
  hasMorning: boolean;
  morningStart: string;
  morningEnd: string;
  hasAfternoon: boolean;
  afternoonStart: string;
  afternoonEnd: string;
  /**
   * Multi-site (M9-3, lot 3B) : dans quelle officine ce jour-là.
   * `null` = pharmacie de rattachement — le cas de tous les mono-site.
   */
  pharmacyId: string | null;
  /**
   * L'officine de l'APRÈS-MIDI quand elle diffère de celle du matin : une même
   * journée peut se partager entre deux officines (« jusqu'à 16h à Saint-Pierre,
   * puis 17h-20h au Palais »). `null` = l'après-midi suit le matin.
   */
  afternoonPharmacyId: string | null;
}

type Grid = Record<string, DayState>; // clé : `${weekType}-${dayOfWeek}`

function emptyDay(def: SlotDefaults = SLOT_FALLBACK): DayState {
  return {
    hasMorning: false,
    morningStart: def.morningStart,
    morningEnd: def.morningEnd,
    hasAfternoon: false,
    afternoonStart: def.afternoonStart,
    afternoonEnd: def.afternoonEnd,
    pharmacyId: null,
    afternoonPharmacyId: null,
  };
}

/** Résout les horaires par défaut d'un jour dans une officine donnée. */
type DefaultsResolver = (pharmacyId: string | null, dayOfWeek: number) => SlotDefaults;

function gridFromTemplate(
  rows: WeeklyTemplateDay[],
  days: { v: number }[],
  resolve: DefaultsResolver
): Grid {
  const g: Grid = {};
  for (const wt of ['A', 'B'] as TemplateWeekType[]) {
    for (const d of days) {
      const r = rows.find((row) => row.weekType === wt && row.dayOfWeek === d.v && row.works);
      const day = emptyDay(resolve(r?.pharmacyId ?? null, d.v));
      if (r) {
        day.hasMorning = !!r.morningStart;
        if (r.morningStart) day.morningStart = r.morningStart;
        if (r.morningEnd) day.morningEnd = r.morningEnd;
        day.hasAfternoon = !!r.afternoonStart;
        if (r.afternoonStart) day.afternoonStart = r.afternoonStart;
        if (r.afternoonEnd) day.afternoonEnd = r.afternoonEnd;
        day.pharmacyId = r.pharmacyId ?? null;
        day.afternoonPharmacyId = r.afternoonPharmacyId ?? null;
      }
      g[`${wt}-${d.v}`] = day;
    }
  }
  return g;
}

function parseTime(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) + (m || 0) / 60;
}

function dayHours(d: DayState): number {
  const m = d.hasMorning ? Math.max(0, parseTime(d.morningEnd) - parseTime(d.morningStart)) : 0;
  const a = d.hasAfternoon ? Math.max(0, parseTime(d.afternoonEnd) - parseTime(d.afternoonStart)) : 0;
  return m + a;
}

function buildRows(mode: ScheduleMode, grid: Grid, days: { v: number }[]): WeeklyTemplateDay[] {
  const rows: WeeklyTemplateDay[] = [];
  const weeks: TemplateWeekType[] = mode === 'ALTERNATING' ? ['A', 'B'] : ['A'];
  for (const wt of weeks) {
    for (const d of days) {
      const st = grid[`${wt}-${d.v}`] ?? emptyDay();
      const works = st.hasMorning || st.hasAfternoon;
      if (!works) continue;
      rows.push({
        weekType: wt,
        dayOfWeek: d.v,
        works: true,
        morningStart: st.hasMorning ? st.morningStart : null,
        morningEnd: st.hasMorning ? st.morningEnd : null,
        afternoonStart: st.hasAfternoon ? st.afternoonStart : null,
        afternoonEnd: st.hasAfternoon ? st.afternoonEnd : null,
        pharmacyId: st.pharmacyId,
        // L'après-midi n'a d'adresse à lui que s'il y a un après-midi ET qu'il
        // se fait ailleurs — le serveur applique la même normalisation.
        afternoonPharmacyId:
          st.hasAfternoon && st.afternoonPharmacyId !== st.pharmacyId
            ? st.afternoonPharmacyId
            : null,
      });
    }
  }
  return rows;
}

export function WeeklyTemplateEditor({
  employeeId,
  contractHours,
  selfMode = false,
  draftSeed,
  onDraftChange,
  draftOpeningHours,
  siteView = 'ALL',
}: {
  employeeId: string;
  contractHours?: number;
  selfMode?: boolean;
  // Mode brouillon (onglet Simulation) : contrôlé, sans fetch ni sauvegarde.
  draftSeed?: { scheduleMode: ScheduleMode; rows: WeeklyTemplateDay[] };
  onDraftChange?: (mode: ScheduleMode, rows: WeeklyTemplateDay[]) => void;
  /**
   * Horaires d'ouverture à utiliser en mode brouillon — le salarié simulé
   * n'existe pas encore, il n'y a donc pas de fiche d'où les tirer.
   */
  draftOpeningHours?: OpeningDay[];
  /**
   * Officine dans laquelle on saisit (bandeau `SiteScopeBar`). `ALL` = la
   * semaine entière, tous sites — c'est le seul cas d'un salarié mono-site.
   */
  siteView?: SiteView;
}) {
  const draftMode = !!onDraftChange;
  const { data, isLoading } = useWeeklyTemplate(draftMode ? undefined : employeeId);
  const saveMut = useSaveWeeklyTemplate(employeeId);
  const reclaimMut = useReclaimManualDays(employeeId);
  /** La fiche, côté titulaire : c'est là seulement qu'on règle, donc qu'on prévient. */
  const ownerView = !draftMode && !selfMode;

  /**
   * Multi-site (lot 3B) : les officines entre lesquelles répartir les jours,
   * rattachement en tête. Liste vide pour un salarié mono-site.
   */
  const { homeId, choices: siteChoices } = useTemplateSites(draftMode ? undefined : employeeId);

  /**
   * Officine d'un jour, normalisée en identifiant : la base note la pharmacie
   * de rattachement `null`, l'écran la nomme comme les autres.
   */
  const scopeOf = (st: DayState): string | undefined => st.pharmacyId ?? homeId;
  /**
   * L'officine de l'après-midi. Une journée peut désormais se partager entre
   * deux officines — c'est la demande du boss : « matin possible de choisir où
   * et aprem possible de choisir où ».
   */
  const afternoonScopeOf = (st: DayState): string | undefined =>
    st.afternoonPharmacyId ?? scopeOf(st);
  /** La journée touche-t-elle cette officine, ne serait-ce qu'à moitié ? */
  const touchesSite = (st: DayState, siteId: string): boolean =>
    (st.hasMorning && scopeOf(st) === siteId) ||
    (st.hasAfternoon && afternoonScopeOf(st) === siteId);
  /** Inverse : le rattachement se stocke `null`, c'est la convention du schéma. */
  const toStored = (siteId: string): string | null => (siteId === homeId ? null : siteId);

  /**
   * Vue « une seule officine ». On retombe sur la vue globale si le bandeau
   * désigne une officine que le salarié ne fréquente plus (case décochée dans
   * l'onglet Identité pendant que l'écran est ouvert).
   */
  const scoped = siteView !== 'ALL' && siteChoices.some((c) => c.id === siteView);
  const scopeName = siteChoices.find((c) => c.id === siteView)?.name ?? '';

  /**
   * Horaires d'ouverture par officine. `null` désigne le rattachement — c'est
   * la même convention que `DayState.pharmacyId`, pour qu'un jour déplacé d'un
   * site à l'autre se pré-remplisse avec les horaires du bon site.
   */
  const hoursOf = useMemo(() => {
    return (pharmacyId: string | null): OpeningDay[] | undefined => {
      if (draftMode) return draftOpeningHours;
      const bySite = data?.openingHours;
      if (!bySite) return undefined;
      const key = pharmacyId ?? data?.homePharmacyId;
      return key ? bySite[key] : undefined;
    };
  }, [draftMode, draftOpeningHours, data]);

  const resolveDefaults = useMemo<DefaultsResolver>(
    () => (pharmacyId, dayOfWeek) => slotDefaults(hoursOf(pharmacyId), dayOfWeek),
    [hoursOf]
  );

  /**
   * Jours proposés. Le dimanche n'apparaît que s'il sert : l'une des officines
   * du salarié ouvre ce jour-là, ou sa semaine type en contient déjà un — sinon
   * un dimanche saisi puis l'officine refermée disparaîtrait à la sauvegarde.
   */
  const showSunday = useMemo(() => {
    const rows = draftMode ? draftSeed?.rows ?? [] : data?.template ?? [];
    const sites: (OpeningDay[] | undefined)[] = draftMode
      ? [draftOpeningHours]
      : Object.values(data?.openingHours ?? {});
    return sites.some((h) => opensOnSunday(h)) || rows.some((r) => r.dayOfWeek === 0 && r.works);
    // `draftSeed` est reconstruit à chaque rendu par le parent : on ne dépend
    // que de ses lignes, sinon la liste des jours changerait d'identité en
    // boucle et re-sèmerait la grille sans fin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftMode, draftSeed?.rows, draftOpeningHours, data]);

  // Dérivée d'un booléen : son identité ne bouge que quand le dimanche
  // apparaît ou disparaît — c'est ce qui rend l'effet de seed ci-dessous sûr.
  const daysToShow = useMemo(
    () => (showSunday ? [...WEEKDAYS, SUNDAY] : WEEKDAYS),
    [showSunday]
  );

  const [mode, setMode] = useState<ScheduleMode>('FIXED');
  const [grid, setGrid] = useState<Grid>({});
  const [activeWeek, setActiveWeek] = useState<TemplateWeekType>('A');
  const [initial, setInitial] = useState<string>('');

  // Seed depuis le backend (mode normal) ou depuis le brouillon (simulation).
  useEffect(() => {
    if (draftMode && draftSeed) {
      const g = gridFromTemplate(draftSeed.rows, daysToShow, resolveDefaults);
      setGrid(g);
      setMode(draftSeed.scheduleMode);
      setInitial(JSON.stringify({ mode: draftSeed.scheduleMode, g }));
    } else if (!draftMode && data) {
      const g = gridFromTemplate(data.template, daysToShow, resolveDefaults);
      setGrid(g);
      setMode(data.scheduleMode);
      setInitial(JSON.stringify({ mode: data.scheduleMode, g }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, draftMode, daysToShow]);

  // En mode brouillon, on remonte les changements au parent.
  useEffect(() => {
    if (draftMode && Object.keys(grid).length > 0) {
      onDraftChange!(mode, buildRows(mode, grid, daysToShow));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, grid]);

  const isDirty = useMemo(
    () => initial !== JSON.stringify({ mode, g: grid }),
    [initial, mode, grid]
  );

  /**
   * Repos fixes que la grille contredit : le jour est coché, avec des horaires,
   * et pourtant le planning le laisse vide — le repos fixe (réglé dans
   * « Changer les horaires ») passe avant la semaine type, et rien ici ne le
   * montrait. Le serveur le lève à l'enregistrement ; l'écran l'annonce avant.
   */
  const fixedOffConflicts = useMemo(() => {
    if (!ownerView) return [];
    const weeks: TemplateWeekType[] = mode === 'ALTERNATING' ? ['A', 'B'] : ['A'];
    return (data?.fixedDaysOff ?? []).filter((dow) =>
      weeks.some((wt) => {
        const st = grid[`${wt}-${dow}`];
        return !!st && (st.hasMorning || st.hasAfternoon);
      })
    );
  }, [ownerView, data, mode, grid]);

  /** Journées à venir figées à la main sur le planning — sans objet en horaires variables. */
  const manualDays = ownerView && data?.scheduleMode !== 'VARIABLE' ? data?.manualDays ?? [] : [];

  function updateDay(week: TemplateWeekType, day: number, patch: Partial<DayState>) {
    setGrid((prev) => ({ ...prev, [`${week}-${day}`]: { ...prev[`${week}-${day}`], ...patch } }));
  }

  /**
   * Changement d'officine sur un jour : les horaires suivent, mais uniquement
   * s'ils sont restés ceux proposés par l'ancien site. Une saisie manuelle du
   * titulaire n'est jamais écrasée.
   */
  function siteMigration(st: DayState, day: number, siteId: string | null): Partial<DayState> {
    const before = resolveDefaults(st.pharmacyId, day);
    const after = resolveDefaults(siteId, day);
    const patch: Partial<DayState> = { pharmacyId: siteId };
    if (st.morningStart === before.morningStart && st.morningEnd === before.morningEnd) {
      patch.morningStart = after.morningStart;
      patch.morningEnd = after.morningEnd;
    }
    if (st.afternoonStart === before.afternoonStart && st.afternoonEnd === before.afternoonEnd) {
      patch.afternoonStart = after.afternoonStart;
      patch.afternoonEnd = after.afternoonEnd;
    }
    return patch;
  }

  function changeDaySite(week: TemplateWeekType, day: number, siteId: string | null) {
    const st = grid[`${week}-${day}`] ?? emptyDay();
    // Déplacer le MATIN ne déplace pas l'après-midi en douce : il garde
    // l'officine qu'on lisait, et redevient implicite si c'est la même.
    const afternoon = afternoonScopeOf(st);
    updateDay(week, day, {
      ...siteMigration(st, day, siteId),
      afternoonPharmacyId: afternoon === (siteId ?? homeId) ? null : afternoon ?? null,
    });
  }

  /** L'officine de l'après-midi seul — le cas d'une journée partagée. */
  function changeAfternoonSite(week: TemplateWeekType, day: number, siteId: string) {
    const st = grid[`${week}-${day}`] ?? emptyDay();
    const morning = scopeOf(st);
    if (siteId === morning) {
      updateDay(week, day, { afternoonPharmacyId: null });
      return;
    }
    // Les horaires suivent l'officine choisie, tant qu'ils sont restés ceux
    // qu'elle proposait — même règle que pour le déplacement d'une journée.
    const before = resolveDefaults(st.afternoonPharmacyId ?? st.pharmacyId, day);
    const after = resolveDefaults(toStored(siteId), day);
    const patch: Partial<DayState> = { afternoonPharmacyId: siteId };
    if (st.afternoonStart === before.afternoonStart && st.afternoonEnd === before.afternoonEnd) {
      patch.afternoonStart = after.afternoonStart;
      patch.afternoonEnd = after.afternoonEnd;
    }
    updateDay(week, day, patch);
  }

  /**
   * Coche ou décoche une demi-journée. Dans une vue « une officine », cocher un
   * jour jusque-là au repos l'attribue **du même geste** à cette officine, avec
   * ses horaires d'ouverture à elle : sans ça, le jour serait créé sur le
   * rattachement et disparaîtrait de la vue où on vient de le saisir.
   */
  function toggleHalf(
    week: TemplateWeekType,
    day: number,
    key: 'hasMorning' | 'hasAfternoon',
    on: boolean
  ) {
    const st = grid[`${week}-${day}`] ?? emptyDay(resolveDefaults(null, day));
    const worked = st.hasMorning || st.hasAfternoon;
    if (on && !worked && scoped && scopeOf(st) !== siteView) {
      updateDay(week, day, { ...siteMigration(st, day, toStored(siteView)), [key]: true });
      return;
    }
    updateDay(week, day, { [key]: on });
  }

  function copyAtoB() {
    setGrid((prev) => {
      const next = { ...prev };
      for (const d of daysToShow) next[`B-${d.v}`] = { ...prev[`A-${d.v}`] };
      return next;
    });
  }

  /**
   * Total de la semaine ; `scope` le restreint à une officine — demi-journée par
   * demi-journée, puisqu'une même journée peut se partager entre deux officines.
   */
  function weekTotal(week: TemplateWeekType, scope?: string): number {
    return daysToShow.reduce((sum, d) => {
      const st = grid[`${week}-${d.v}`] ?? emptyDay();
      if (!scope) return sum + dayHours(st);
      let h = 0;
      if (st.hasMorning && scopeOf(st) === scope) {
        h += Math.max(0, parseTime(st.morningEnd) - parseTime(st.morningStart));
      }
      if (st.hasAfternoon && afternoonScopeOf(st) === scope) {
        h += Math.max(0, parseTime(st.afternoonEnd) - parseTime(st.afternoonStart));
      }
      return sum + h;
    }, 0);
  }

  async function handleSave() {
    await saveMut.mutateAsync({ scheduleMode: mode, rows: buildRows(mode, grid, daysToShow) });
    setInitial(JSON.stringify({ mode, g: grid }));
  }

  /** Une journée figée à la main, en clair : ses horaires et, en multi-site, où. */
  function describeManualDay(m: ManualDay): string {
    if (!m.worked) return 'retirée, ne travaille pas';
    const nameOf = (id: string) => siteChoices.find((c) => c.id === id)?.name;
    const parts: string[] = [];
    if (m.morningStart) parts.push(`${m.morningStart}–${m.morningEnd}`);
    if (m.afternoonStart) parts.push(`${m.afternoonStart}–${m.afternoonEnd}`);
    let where = '';
    if (siteChoices.length > 1) {
      where =
        m.morningStart && m.afternoonStart && m.siteId !== m.afternoonSiteId
          ? ` · matin ${nameOf(m.siteId) ?? ''}, après-midi ${nameOf(m.afternoonSiteId) ?? ''}`
          : ` · ${nameOf(m.morningStart ? m.siteId : m.afternoonSiteId) ?? ''}`;
    }
    return `${parts.join(' + ')}${where}`;
  }

  if (isLoading) {
    return <div className="text-[13px] text-slate-500 py-8 text-center">Chargement de la semaine type…</div>;
  }

  /**
   * Le salarié ne règle pas ses propres horaires : en fixe comme en alterné,
   * sa semaine type EST son planning, et c'est le titulaire qui la tient. Il la
   * lit ici, sans pouvoir y toucher. Seul le mode VARIABLE lui laisse la main,
   * parce que la grille n'y est qu'une fenêtre de disponibilité. Le serveur
   * refuse de toute façon (403) : l'écran évite juste de proposer une saisie
   * vouée à l'échec. On se fie au mode ENREGISTRÉ, pas au mode affiché.
   */
  const readOnly = selfMode && !draftMode && data?.scheduleMode !== 'VARIABLE';

  const weeksToShow: TemplateWeekType[] = mode === 'ALTERNATING' ? ['A', 'B'] : ['A'];
  const shownWeek = mode === 'ALTERNATING' ? activeWeek : 'A';
  const total = weekTotal(shownWeek, scoped ? siteView : undefined);
  const totalAllSites = weekTotal(shownWeek);

  return (
    <div className="space-y-5">
      {/* Bandeau explicatif */}
      {!draftMode && (
      <div className="bg-primary-bg/40 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
        <CalendarRange size={16} className="text-primary mt-0.5 flex-shrink-0" />
        <div className="text-[12px] text-slate-700 leading-relaxed">
          {mode === 'VARIABLE' ? (
            selfMode ? (
              <>Vous n'avez pas d'horaires fixes. Indiquez ici les <strong>jours et heures
              où vous <em>pouvez</em> venir</strong> : le planning vous placera uniquement
              là où l'équipe a besoin de renfort, dans cette fenêtre. Si personne n'est
              disponible sur un manque, vous pouvez être sollicité en dehors — vous recevrez
              alors une notification à confirmer.</>
            ) : (
              <>Ce salarié est en <strong>horaires variables</strong>. La grille ci-dessous
              n'est pas un engagement : c'est la <strong>fenêtre de disponibilité</strong>
              (« il peut venir tel jour, entre telle et telle heure »). Le moteur le place
              ensuite sur les <strong>creux de couverture</strong> depuis le planning.</>
            )
          ) : selfMode ? (
            <>Vos horaires sont <strong>fixés par votre titulaire</strong> : voici votre
            semaine type, telle qu'il l'a réglée. Pour une exception ponctuelle, déclarez
            une <strong>indisponibilité</strong> ou un <strong>congé</strong> ; pour un
            changement durable, voyez-le avec lui.</>
          ) : (
            <>La <strong>semaine type</strong> définit les jours et horaires par défaut du
            salarié. C'est la base utilisée par le bouton <strong>« Générer le planning »</strong>.
            Les repos fixes, indisponibilités et congés restent gérés à part et l'emportent.</>
          )}
        </div>
      </div>
      )}

      {/* Mode : semaine fixe vs alternée. Le rythme se décide par le
          titulaire : le salarié ne le voit pas, même en variable. */}
      {!selfMode && (
      <div>
        <div className="label mb-2">Rythme de la semaine</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setMode('FIXED')}
            className={`p-3 rounded-xl border text-left transition-all ${mode === 'FIXED' ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'}`}
          >
            <div className="text-[13px] font-semibold text-slate-900">Semaine fixe</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Les mêmes horaires chaque semaine</div>
          </button>
          <button
            type="button"
            onClick={() => setMode('ALTERNATING')}
            className={`p-3 rounded-xl border text-left transition-all ${mode === 'ALTERNATING' ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'}`}
          >
            <div className="text-[13px] font-semibold text-slate-900">2 semaines alternées</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Semaine A (paires) / B (impaires)</div>
          </button>
          {/* Horaires variables : pas d'engagement, le moteur comble les creux. */}
          <button
            type="button"
            onClick={() => setMode('VARIABLE')}
            className={`p-3 rounded-xl border text-left transition-all ${mode === 'VARIABLE' ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'}`}
          >
            <div className="text-[13px] font-semibold text-slate-900">Horaires variables</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Selon les besoins — comble les trous</div>
          </button>
        </div>
      </div>
      )}

      {/* Onglets semaine A / B en mode alterné */}
      {mode === 'ALTERNATING' && (
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {weeksToShow.map((wt) => (
              <button
                key={wt}
                type="button"
                onClick={() => setActiveWeek(wt)}
                className={`px-3.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${shownWeek === wt ? 'bg-white shadow-sm text-primary' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Semaine {wt} {wt === 'A' ? '(paires)' : '(impaires)'}
              </button>
            ))}
          </div>
          {!readOnly && (
          <button
            type="button"
            onClick={copyAtoB}
            className="flex items-center gap-1.5 text-[11.5px] text-slate-600 hover:text-primary font-medium"
            title="Copier la semaine A vers la semaine B"
          >
            <Copy size={13} /> Copier A → B
          </button>
          )}
        </div>
      )}

      {/* Ce qui, hors de cette grille, décide encore du planning. Sans ces deux
          bandeaux, on changeait ici des horaires qui ne « prenaient » pas, sans
          rien à l'écran pour dire pourquoi. */}
      {fixedOffConflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-[12px] text-amber-900 flex items-start gap-2.5">
          <AlertTriangle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="leading-relaxed">
            {fixedOffConflicts.length === 1 ? 'Le ' : 'Les '}
            <strong>{listDays(fixedOffConflicts)}</strong>{' '}
            {fixedOffConflicts.length === 1 ? 'est aussi posé' : 'sont aussi posés'} en{' '}
            <strong>repos fixe</strong> (réglé dans « Changer les horaires ») : tant que c'est
            le cas, le planning laisse ce jour vide, quels que soient les horaires saisis ici.{' '}
            <strong>Le repos fixe sera levé à l'enregistrement.</strong>
          </div>
        </div>
      )}

      {manualDays.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-[12px] text-amber-900">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 leading-relaxed">
              {manualDays.length === 1
                ? 'Une journée à venir a été réglée à la main sur le planning'
                : `${manualDays.length} journées à venir ont été réglées à la main sur le planning`}{' '}
              (heures ajoutées, journée basculée ou retirée) : elles gardent ce réglage et{' '}
              <strong>ne suivent pas la semaine type</strong> ci-dessous.
              <ul className="mt-1.5 space-y-0.5">
                {manualDays.slice(0, 6).map((m) => (
                  <li key={m.date} className="tabular-nums">
                    <span className="font-medium">
                      {format(new Date(`${m.date}T00:00:00`), 'EEE d MMM', { locale: fr })}
                    </span>{' '}
                    — {describeManualDay(m)}
                  </li>
                ))}
                {manualDays.length > 6 && (
                  <li className="text-amber-700">… et {manualDays.length - 6} autre{manualDays.length - 6 > 1 ? 's' : ''}</li>
                )}
              </ul>
            </div>
          </div>
          <div className="mt-2.5 flex items-center justify-end gap-3">
            {reclaimMut.error && (
              <span className="text-danger flex items-center gap-1">
                <AlertCircle size={13} /> {(reclaimMut.error as Error).message}
              </span>
            )}
            <Button
              variant="default"
              size="sm"
              icon={RotateCcw}
              loading={reclaimMut.isPending}
              onClick={() => reclaimMut.mutate()}
            >
              {manualDays.length === 1 ? 'La remettre' : 'Les remettre'} sur la semaine type
            </Button>
          </div>
        </div>
      )}
      {ownerView && reclaimMut.isSuccess && manualDays.length === 0 && (
        <div className="text-[12px] text-primary flex items-center gap-1">
          <Check size={13} />
          {reclaimMut.data.reclaimed > 1
            ? `${reclaimMut.data.reclaimed} journées remises sur la semaine type.`
            : 'Journée remise sur la semaine type.'}
        </div>
      )}

      {/* Grille des jours */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
        {daysToShow.map((d) => {
          const st = grid[`${shownWeek}-${d.v}`] ?? emptyDay(resolveDefaults(null, d.v));
          const works = st.hasMorning || st.hasAfternoon;
          // Vue « une officine » : un jour qui se fait ENTIÈREMENT ailleurs se
          // montre mais ne se saisit pas. Le bouton « Déplacer ici » est la
          // seule façon de le récupérer, et il le retire de l'autre.
          //
          // Une journée peut maintenant se partager entre deux officines : si
          // l'une de ses moitiés se fait ici, elle reste éditable, et c'est le
          // sélecteur de la demi-journée qui dit où va l'autre.
          const elsewhere = scoped && works && !touchesSite(st, siteView);
          const elsewhereName = siteChoices.find((c) => c.id === scopeOf(st))?.name ?? '';
          const afternoonName =
            siteChoices.find((c) => c.id === afternoonScopeOf(st))?.name ?? '';
          const split = st.hasMorning && st.hasAfternoon && afternoonScopeOf(st) !== scopeOf(st);
          return (
            <div key={d.v} className="p-3.5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[13px] font-semibold w-24 ${works ? 'text-slate-900' : 'text-slate-400'}`}>{d.l}</span>
                  {works ? (
                    <span className="text-[10.5px] text-primary bg-primary-bg rounded px-1.5 py-0.5 tabular-nums font-medium">
                      {dayHours(st).toFixed(1)}h
                    </span>
                  ) : (
                    <span className="text-[10.5px] text-slate-400 italic">
                      {ownerView && data?.fixedDaysOff?.includes(d.v) ? 'Repos fixe' : 'Repos'}
                    </span>
                  )}
                  {works && fixedOffConflicts.includes(d.v) && (
                    <span
                      title="Ce jour est aussi un repos fixe : il sera levé à l'enregistrement"
                      className="text-[10.5px] text-amber-800 bg-amber-100 rounded px-1.5 py-0.5 font-medium"
                    >
                      repos fixe
                    </span>
                  )}
                </div>
                {/* Multi-site (lot 3B) : dans quelle officine ce jour-là.
                    N'apparaît que pour un salarié partagé, et que sur les jours
                    travaillés — un jour de repos n'appartient à aucun site.
                    En vue globale on choisit ; en vue d'une officine on lit. */}
                {elsewhere ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                      Chez {elsewhereName}
                    </span>
                    <button
                      type="button"
                      onClick={() => changeDaySite(shownWeek, d.v, toStored(siteView))}
                      className="text-[11px] font-medium text-primary hover:underline"
                      title={`Déplacer ce jour vers ${scopeName}`}
                    >
                      Déplacer ici
                    </button>
                  </div>
                ) : readOnly && siteChoices.length > 1 && works ? (
                  /* Lecture seule : on dit où, sans proposer de le changer. */
                  <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                    {split
                      ? `Matin ${elsewhereName} · après-midi ${afternoonName}`
                      : `Chez ${st.hasMorning ? elsewhereName : afternoonName}`}
                  </span>
                ) : siteChoices.length > 1 && works && !scoped ? (
                  /* Une officine par DEMI-JOURNÉE : « matin possible de choisir
                     où et aprem possible de choisir où ». Le second sélecteur
                     n'apparaît que s'il y a un après-midi à placer. */
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {st.hasMorning && (
                      <label className="flex items-center gap-1 text-[10.5px] text-slate-400">
                        {st.hasAfternoon && 'matin'}
                        <select
                          value={scopeOf(st) ?? ''}
                          onChange={(e) => changeDaySite(shownWeek, d.v, toStored(e.target.value))}
                          title="Officine où il travaille le matin"
                          className="text-[11px] border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 max-w-[170px]"
                        >
                          {siteChoices.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    {st.hasAfternoon && (
                      <label className="flex items-center gap-1 text-[10.5px] text-slate-400">
                        {st.hasMorning && 'après-midi'}
                        <select
                          value={afternoonScopeOf(st) ?? ''}
                          onChange={(e) => changeAfternoonSite(shownWeek, d.v, e.target.value)}
                          title="Officine où il travaille l'après-midi"
                          className="text-[11px] border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 max-w-[170px]"
                        >
                          {siteChoices.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                ) : split && scoped ? (
                  /* Vue d'une officine : dire d'où à où va la journée. */
                  <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                    Matin {elsewhereName} · après-midi {afternoonName}
                  </span>
                ) : null}
              </div>
              <div className={`grid grid-cols-2 gap-3 ${elsewhere ? 'opacity-50' : ''}`}>
                <HalfDay
                  label="Matin"
                  active={st.hasMorning}
                  start={st.morningStart}
                  end={st.morningEnd}
                  locked={elsewhere || readOnly}
                  onToggle={(v) => toggleHalf(shownWeek, d.v, 'hasMorning', v)}
                  onStart={(v) => updateDay(shownWeek, d.v, { morningStart: v })}
                  onEnd={(v) => updateDay(shownWeek, d.v, { morningEnd: v })}
                />
                <HalfDay
                  label="Après-midi"
                  active={st.hasAfternoon}
                  start={st.afternoonStart}
                  end={st.afternoonEnd}
                  locked={elsewhere || readOnly}
                  onToggle={(v) => toggleHalf(shownWeek, d.v, 'hasAfternoon', v)}
                  onStart={(v) => updateDay(shownWeek, d.v, { afternoonStart: v })}
                  onEnd={(v) => updateDay(shownWeek, d.v, { afternoonEnd: v })}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Total + bouton enregistrer */}
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-600">
          {scoped
            ? scopeName
            : mode === 'VARIABLE'
              ? 'Amplitude disponible'
              : `Total semaine ${mode === 'ALTERNATING' ? shownWeek : ''}`} :{' '}
          <strong className="text-slate-900 tabular-nums">{total.toFixed(1)}h</strong>
          {/* En vue « une officine », le total du site ne se compare pas aux
              heures contractuelles : c'est le cumul des deux qui les remplit. */}
          {scoped && (
            <span className="text-slate-500">
              {' '}· toutes officines :{' '}
              <strong className="text-slate-700 tabular-nums">{totalAllSites.toFixed(1)}h</strong>
            </span>
          )}
          {contractHours ? <span className="text-slate-400"> / {contractHours}h contractuelles</span> : null}
        </div>
        {!draftMode && !readOnly && (
          <div className="flex items-center gap-3">
            {saveMut.isSuccess && !isDirty && (
              <span className="text-[12px] text-primary flex items-center gap-1">
                <Check size={13} /> Enregistré
                {/* Ce que l'enregistrement a fait au-delà de la grille : le dire,
                    pour qu'on sache pourquoi le planning a bougé. */}
                {!!saveMut.data?.liftedDaysOff?.length &&
                  ` · repos fixe du ${listDays(saveMut.data.liftedDaysOff)} levé`}
                {!!saveMut.data?.reclaimedManual &&
                  ` · ${saveMut.data.reclaimedManual} journée${saveMut.data.reclaimedManual > 1 ? 's' : ''} réglée${saveMut.data.reclaimedManual > 1 ? 's' : ''} à la main reprise${saveMut.data.reclaimedManual > 1 ? 's' : ''}`}
              </span>
            )}
            {/* Un repos fixe en conflit suffit à rendre l'enregistrement utile :
                la grille n'a pas bougé, mais c'est lui qui la contredit. */}
            <Button
              variant="primary"
              icon={Save}
              loading={saveMut.isPending}
              disabled={!isDirty && fixedOffConflicts.length === 0}
              onClick={handleSave}
            >
              {isDirty
                ? 'Enregistrer la semaine type'
                : fixedOffConflicts.length > 0
                  ? 'Lever le repos fixe'
                  : 'Aucun changement'}
            </Button>
          </div>
        )}
      </div>

      {!draftMode && saveMut.error && (
        <div className="bg-danger-bg border border-danger/30 rounded-lg p-3 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} /> {(saveMut.error as Error).message}
        </div>
      )}

      {mode === 'ALTERNATING' && (
        <div className="text-[11px] text-slate-500 flex items-start gap-2">
          <RefreshCw size={12} className="mt-0.5 flex-shrink-0" />
          La semaine A s'applique aux semaines <strong>paires</strong> (n° ISO), la semaine B aux <strong>impaires</strong>.
        </div>
      )}

      {mode === 'VARIABLE' && (
        <div className="text-[11px] text-slate-500 flex items-start gap-2">
          <RefreshCw size={12} className="mt-0.5 flex-shrink-0" />
          Ces heures ne sont <strong>pas</strong> planifiées d'office : elles délimitent quand
          la personne peut être appelée en renfort. Le calcul se lance depuis le planning
          (bouton <strong>« Combler les trous »</strong>) ou à la validation d'une absence.
        </div>
      )}
    </div>
  );
}

function HalfDay({
  label, active, start, end, locked = false, onToggle, onStart, onEnd,
}: {
  label: string;
  active: boolean;
  start: string;
  end: string;
  /** Jour tenu par une autre officine : lisible, pas modifiable ici. */
  locked?: boolean;
  onToggle: (v: boolean) => void;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
}) {
  return (
    <div className={`rounded-lg border p-2.5 ${active ? 'border-slate-200 bg-slate-50/60' : 'border-slate-100 bg-white'}`}>
      <label className={`flex items-center gap-1.5 text-[11.5px] font-medium mb-2 ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={active} disabled={locked} onChange={(e) => onToggle(e.target.checked)} className="rounded" />
        <span className={active ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
      </label>
      {active && (
        <div className="grid grid-cols-2 gap-2">
          <input type="time" value={start} disabled={locked} onChange={(e) => onStart(e.target.value)} className="input tabular-nums text-center !py-1.5" />
          <input type="time" value={end} disabled={locked} onChange={(e) => onEnd(e.target.value)} className="input tabular-nums text-center !py-1.5" />
        </div>
      )}
    </div>
  );
}
